import { sortTagsBySequence } from './tagUtils';

export const normalizeMealTagSequence = (sequence) => {
  const numericSequence = Number(sequence);

  if (!Number.isFinite(numericSequence) || numericSequence < 1) {
    return 1;
  }

  return Math.trunc(numericSequence);
};

export const mapApiTagToMealTag = (tag = {}) => ({
  id: tag?.id,
  english: tag?.nameEn?.trim?.() ?? '',
  nor: tag?.nameNo?.trim?.() ?? '',
  sequence: normalizeMealTagSequence(tag?.displayOrder),
});

export const getMealAssignedTags = (meal = {}) => {
  const assignedTags = meal?.assignedTags ?? [];

  return sortTagsBySequence(assignedTags.map((tag) => mapApiTagToMealTag(tag)));
};

export const getMealAssignedTagTitles = (meal = {}, language = 'English') => {
  return getMealAssignedTags(meal)
    .map((tag) => (language === 'English' ? tag?.english : tag?.nor))
    .filter(Boolean);
};

export const formatMealTagLabel = (tag = {}, language = 'English') => {
  const tagName = language === 'English' ? tag?.english : tag?.nor;
  return `${normalizeMealTagSequence(tag?.sequence)} - ${tagName ?? ''}`;
};

export const areMealTagsEqual = (firstTag = {}, secondTag = {}) => {
  if (firstTag?.id && secondTag?.id) {
    return firstTag.id === secondTag.id;
  }

  return (
    (firstTag?.english ?? '') === (secondTag?.english ?? '') &&
    (firstTag?.nor ?? '') === (secondTag?.nor ?? '') &&
    normalizeMealTagSequence(firstTag?.sequence) ===
      normalizeMealTagSequence(secondTag?.sequence)
  );
};

export const dedupeMealTags = (tags = []) => {
  return tags.filter(
    (tag, index) =>
      tags.findIndex((candidate) => areMealTagsEqual(candidate, tag)) === index
  );
};

export const findMatchingApiTag = (apiTags = [], payload = {}) => {
  const normalizedEnglish = payload?.nameEn?.trim?.().toLowerCase() ?? '';
  const normalizedNorwegian = payload?.nameNo?.trim?.().toLowerCase() ?? '';

  return apiTags.find(
    (tag) =>
      (tag?.nameEn?.trim?.().toLowerCase() ?? '') === normalizedEnglish &&
      (tag?.nameNo?.trim?.().toLowerCase() ?? '') === normalizedNorwegian
  );
};

export const getMealAssignedTagIds = (tags = [], availableTags = []) => {
  return dedupeMealTags(tags)
    .map((tag) => {
      if (tag?.id) {
        return tag.id;
      }

      return findMatchingApiTag(availableTags, {
        nameEn: tag?.english,
        nameNo: tag?.nor,
      })?.id;
    })
    .filter(Boolean);
};

export const getMealAssignedTagIdsFromMeal = (meal = {}) => {
  return dedupeMealTags(getMealAssignedTags(meal))
    .map((tag) => tag?.id)
    .filter(Boolean);
};

const isTagManagedFoodBox = (meal = {}) => {
  return meal?.isCateringFood || (meal?.isAddOnItem ?? false);
};

export const getMealsUsingTag = (meals = [], tagId) => {
  if (!tagId) {
    return [];
  }

  return meals.filter(
    (meal) =>
      isTagManagedFoodBox(meal) &&
      getMealAssignedTagIdsFromMeal(meal).includes(tagId)
  );
};

export const buildTagRemovalMealUpdates = (meals = [], tagId) => {
  return getMealsUsingTag(meals, tagId)
    .map((meal) => ({
      externalId: meal?.externalId,
      assignedTagIds: getMealAssignedTagIdsFromMeal(meal).filter(
        (assignedTagId) => assignedTagId !== tagId
      ),
    }))
    .filter((mealUpdate) => Boolean(mealUpdate.externalId));
};

export const getNextTagDisplayOrder = (apiTags = []) => {
  if (!apiTags.length) {
    return 1;
  }

  return (
    Math.max(
      ...apiTags.map((tag) =>
        Number.isFinite(Number(tag?.displayOrder)) ? Number(tag.displayOrder) : 0
      )
    ) + 1
  );
};
